/** @jsxImportSource @emotion/react */

import { Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import { AnnouncementListItem } from "../types";
import { mapStatusToText } from "./utils";

interface Props {
  status: AnnouncementListItem["status"];
}

/**
 * AnnouncementStatus component displays the status of an announcement with an icon and text.
 *
 * @param {Props} props - The props for the component.
 * @param {AnnouncementListItem["status"]} props.status - The status of the announcement.
 *
 * @returns {JSX.Element | null} The rendered component or null if the status is invalid.
 */
export const AnnouncementStatus = ({ status }: Props) => {
  if (typeof status !== "string") {
    return null;
  }
  const {
    displayText,
    iconType,
    iconColor = Colors.Neutral900,
  } = mapStatusToText(status) || {};
  if (iconType === undefined) {
    return null;
  }
  return (
    <Flex alignItems="center" gap={8} css={{ height: "100%" }}>
      <Icon
        type={iconType}
        color={iconColor}
        data-testid="announcement-status-icon"
        css={{ width: 10, height: 10 }}
      />
      <Text
        data-testid="announcement-status-label"
        css={{ paddingTop: SPACING.X_SMALL }}
        marginBottom="xs"
      >
        {displayText}
      </Text>
    </Flex>
  );
};
